import localforage from 'localforage';

export function initIndexDatabase({
	stores = [],
	databaseName = 'workinance',
	options,
}: {
	stores?: string[];
	databaseName?: string;
	options?: Omit<LocalForageOptions, 'storeName' | 'name'>;
} = {}) {
	// create one instance per store (like tables)
	const storeInstances: Record<string, LocalForage> = {};

	stores.forEach((store) => {
		storeInstances[store] = localforage.createInstance({
			name: databaseName,
			storeName: store,
			description: options?.description ?? 'Browser database',
			...options,
		});
	});

	// localforage needs a browser. During SSR or tests, reads find nothing and writes do nothing.
	const isBrowser = typeof window !== 'undefined';

	function getStore(storeName: string) {
		const store = storeInstances[storeName];
		if (!store) {
			throw new Error(`Store "${storeName}" not initialised`);
		}
		return store;
	}

	return {
		put: async <TData>({
			key,
			storeName,
			data,
		}: {
			key: string;
			storeName: string;
			data: TData;
		}) => {
			if (!isBrowser) return;
			const store = getStore(storeName);
			await store.setItem(key, data);
		},

		get: async <TData>({
			key,
			storeName,
		}: {
			key: string;
			storeName: string;
		}) => {
			if (!isBrowser) return null;
			const store = getStore(storeName);
			return (await store.getItem<TData>(key)) ?? null;
		},

		/** Everything in one store as a plain object of key and value. Empty when the store has nothing. */
		getAll: async ({
			storeName,
		}: {
			storeName: string;
		}): Promise<Record<string, unknown>> => {
			if (!isBrowser) return {};
			const entries: Record<string, unknown> = {};
			await getStore(storeName).iterate((value, key) => {
				entries[key] = value;
			});
			return entries;
		},

		/** Makes the store hold exactly these entries: each is saved under its own key and any other key is removed. */
		replaceAll: async ({
			storeName,
			entries,
		}: {
			storeName: string;
			entries: Record<string, unknown>;
		}) => {
			if (!isBrowser) return;
			const store = getStore(storeName);
			await Promise.all(
				Object.entries(entries).map(([key, value]) =>
					store.setItem(key, value),
				),
			);
			const staleKeys = (await store.keys()).filter((key) => !(key in entries));
			await Promise.all(staleKeys.map((key) => store.removeItem(key)));
		},

		clear: async ({ storeName }: { storeName: string }) => {
			if (!isBrowser) return;
			await getStore(storeName).clear();
		},

		remove: async ({ key, storeName }: { key: string; storeName: string }) => {
			if (!isBrowser) return;
			const store = getStore(storeName);
			await store.removeItem(key);
		},
	};
}

/** The one browser database shared by every app in the monorepo. */
export const database = initIndexDatabase({
	stores: ['auth', 'business', 'last-sign-in', 'pos-open-sales'],
});
